import { Fragment, useRef } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { Form } from "@components/common/Form"
import LabeledTextField from "@components/common/LabeledTextField"
import { useResponsiveValue } from "@theme-ui/match-media"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { useUI } from "@components/ui/context"
import { ConfiguratorEstimateForm } from "@lib/marketing-forms/validations"
import { FORM_ERROR, FORM_SUCCESS } from "@components/common/Form"
import { Step } from "@components/configurator/ConfiguratorContext"
import { appToast } from "@components/common/Toast"
import { sendEmail } from "./Configurator/ConfguratorEmailTemplate"
import KlaviyoClient from "@integrations/klaviyo"

export interface ConfiguratorEstimateModalProps {
  title: string
  configuratorEstimateModalIsOpen: boolean
  setConfiguratorEstimateModalIsOpen: (configuratorEstimateModalIsOpen: boolean) => void
  currentFormattedTotalPrice: () => string
  steps: Step[]
}

export const ConfiguratorEstimateModal: React.FC<ConfiguratorEstimateModalProps> = ({
  title,
  configuratorEstimateModalIsOpen,
  setConfiguratorEstimateModalIsOpen,
  currentFormattedTotalPrice,
  steps,
}: ConfiguratorEstimateModalProps) => {
  const initialFocusEl = useRef(null)
  const { logo } = useUI()

  return (
    <>
      <Transition.Root show={configuratorEstimateModalIsOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setConfiguratorEstimateModalIsOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <div>
                  <Dialog.Panel
                    className={`relative w-auto transform overflow-hidden rounded-l bg-gray-800  px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:max-w-${useResponsiveValue(
                      ["80%", 500]
                    )} sm:p-6`}
                  >
                    <div className="absolute right-0 top-0 pr-4 pt-4">
                      <button
                        type="button"
                        className="rounded-md bg-gray-800 text-gray-100 hover:text-gray-400 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        onClick={() => setConfiguratorEstimateModalIsOpen(false)}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="sm:flex sm:justify-center mx-5">
                      <div ref={initialFocusEl}>
                        <div className="flex items-center border-b border-white pb-4">
                          {/* Home Button */}
                          <div className="sm:basis-1/2 flex rounded-full">
                            {logo && logo.image && (
                              <img
                                className="h-6 w-auto pr-8"
                                src={logo.image}
                                alt="New Way Van Life"
                              />
                            )}
                          </div>

                          {/* Title */}
                          <div className="sm:basis-1/2 flex grow flex-col space-y-4 justify-center">
                            <h1 className="text-white text-lg lg:text-2xl py-1 text-center text-nowrap">
                              <div className="flex-col items-center">
                                <h1 className="text-3xl text-white text-center">{title}</h1>
                              </div>
                            </h1>
                          </div>
                        </div>

                        {/* Total Price */}
                        <div className="flex justify-center w-full py-4">
                          <div className="flex-col items-center">
                            <div className="text-2xl text-white font-bold text-center">
                              ESTIMATE
                            </div>
                            <div
                              aria-label="price"
                              className="text-xl lg:text-5xl font-normal text-white"
                            >
                              {currentFormattedTotalPrice()}
                            </div>
                          </div>
                        </div>

                        <hr />
                        <Form
                          className="mt-2 space-y-4"
                          initialValues={{ fullName: "", email: "", phone: "" }}
                          schema={ConfiguratorEstimateForm}
                          onSubmit={async (values) => {
                            try {
                              const response = await sendEmail(
                                values,
                                title,
                                currentFormattedTotalPrice(),
                                steps
                              )

                              if (response.status === 200) {
                                setConfiguratorEstimateModalIsOpen(false)
                                appToast({
                                  type: "success",
                                  message: "Estimate Sent!",
                                })
                              } else {
                                appToast({
                                  type: "error",
                                  message: "Estimate failed to send.",
                                })
                              }

                              const { email, fullName, phone } = values
                              KlaviyoClient.public.identify({
                                email: email,
                                properties: {
                                  $first_name: fullName.split(" ")[0] || "",
                                  $last_name: fullName.split(" ").slice(1).join(" ") || "",
                                  phone_number: `+1${phone}`,
                                },
                                post: true,
                              })
                              KlaviyoClient.public.track({
                                event: "Requested Estimate",
                                email,
                              })
                            } catch (error: any) {
                              appToast({
                                type: "error",
                                message: "Estimate failed to send. Error: " + error.toString(),
                              })

                              return {
                                [FORM_ERROR]: error.toString(),
                              }
                            }
                          }}
                        >
                          <div className="text-white placeholder-gray-500">Full Name</div>
                          <LabeledTextField
                            type="text"
                            name="fullName"
                            label=""
                            placeholder="Full Name"
                            hiddenLabel={false}
                          />
                          <div className="text-white placeholder-gray-500">Email</div>
                          <LabeledTextField
                            type="email"
                            name="email"
                            label=""
                            placeholder="Email"
                            hiddenLabel={false}
                          />
                          <div className="text-white placeholder-gray-500">
                            Phone Number (Optional)
                          </div>
                          <LabeledTextField
                            type="text"
                            name="phone"
                            label=""
                            placeholder="Phone Number"
                            hiddenLabel={false}
                          />
                          <div className="flex justify-center">
                            <button
                              type="submit"
                              className="modal-close-button inline-flex items-center gap-x-1.5 rounded-2xl bg-primary px-3 py-2 mt-4 mr-2 text-sm font-semibold shadow-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary bg-white border border-black"
                            >
                              Submit
                            </button>
                          </div>
                        </Form>
                      </div>
                    </div>
                  </Dialog.Panel>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}

export default ConfiguratorEstimateModal
